import { Injectable, inject } from '@angular/core';
import { sum } from 'lodash';

import { BoardColumn, Sorting, isCostColumn } from '@main-application/boards/interfaces/board';
import { BoardColumnType, ScheduleCellStatus } from '@main-application/boards/interfaces/board.enums';
import { ProjectWorkflowStepEnumLabels, WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { getTimeInStep } from '@shared/functions/get-time-in-step.function';
import { RestTurnoverTaskModel } from '@shared/interfaces/turnover.interface';
import { DaysInStepPipe } from '@shared/pipes/days-in-step.pipe';

export enum BoardScheduleColumns {
  Due = 'Due',
  Assignee = 'Assignee',
  Status = 'Status',
  PONumber = 'PONumber',
}

@Injectable({
  providedIn: 'root',
})
export class CellValueHelper {
  private daysInStep: DaysInStepPipe = inject(DaysInStepPipe);

  applyValue(
    column: BoardColumn,
    fieldName: string,
    data: RestTurnoverTaskModel,
    newValue: any,
    shouldBeRemoved = false
  ) {
    switch (column.type) {
      case BoardColumnType.Notes:
      case BoardColumnType.DropDown: {
        let { additionalData } = data.turnoverData;
        additionalData = additionalData || {};
        additionalData.values = additionalData.values || [];
        if (shouldBeRemoved) {
          additionalData.values = additionalData.values.filter(x => x.id !== column.id);
        } else {
          const obj = additionalData?.values?.find(x => x.id === column.id);
          if (obj) {
            obj.value = newValue.value;
            obj.name = newValue.name;
          } else {
            additionalData?.values.push(newValue);
          }
        }

        data.turnoverData.additionalData = additionalData;
        break;
      }
      case BoardColumnType.CustomDate: {
        let { additionalData } = data.turnoverData;
        additionalData = additionalData || {};
        additionalData.dates = additionalData.dates || [];
        if (shouldBeRemoved) {
          additionalData.dates = additionalData.dates.filter(x => x.id !== column.id);
        } else {
          const obj = additionalData?.dates?.find(x => x.id === column.id);
          if (obj) {
            obj.date = newValue.date;
          } else {
            additionalData?.dates.push(newValue);
          }
        }

        data.turnoverData.additionalData = additionalData;
        break;
      }
      case BoardColumnType.Budget:
      case BoardColumnType.CustomAmount: {
        let { additionalData } = data.turnoverData;
        additionalData = additionalData || {};
        additionalData.amounts = additionalData.amounts || [];
        if (shouldBeRemoved) {
          additionalData.amounts = additionalData.amounts.filter(x => x.id !== column.id);
        } else {
          const obj = additionalData?.amounts?.find(x => x.id === column.id);
          if (obj) {
            obj.amount = newValue.amount;
          } else {
            additionalData?.amounts.push(newValue);
          }
        }
        data.turnoverData.additionalData = additionalData;
        break;
      }
      default: {
        data.turnoverData[fieldName] = newValue;
      }
    }
  }

  transformValue(column: BoardColumn, newValue: any, name = ''): any {
    switch (column.type) {
      case BoardColumnType.DropDown: {
        return {
          id: column.id,
          name,
          value: newValue,
        };
      }
      case BoardColumnType.CustomDate: {
        return {
          id: column.id,
          name,
          date: newValue,
        };
      }
      case BoardColumnType.Budget:
      case BoardColumnType.CustomAmount: {
        return {
          id: column.id,
          name,
          amount: newValue,
        };
      }
      default: {
        return newValue;
      }
    }
  }

  transformPath(column: BoardColumn, fieldName: string, hasValue: boolean): any {
    switch (column.type) {
      case BoardColumnType.Notes:
      case BoardColumnType.DropDown: {
        return !hasValue ? '/values/-' : `/values/${column.id}`;
      }
      case BoardColumnType.CustomDate: {
        return !hasValue ? '/dates/-' : `/dates/${column.id}`;
      }
      case BoardColumnType.Budget:
      case BoardColumnType.CustomAmount: {
        return !hasValue ? '/amounts/-' : `/amounts/${column.id}`;
      }
      default: {
        return fieldName;
      }
    }
  }

  turnoverHasValue(column: BoardColumn, data: RestTurnoverTaskModel): boolean {
    const { type } = column;
    switch (type) {
      case BoardColumnType.Budget:
      case BoardColumnType.CustomAmount: {
        const { additionalData } = data.turnoverData;
        const item = additionalData?.amounts?.find(x => x.id === column.id);
        return !!item;
      }
      case BoardColumnType.DropDown: {
        const { additionalData } = data.turnoverData;
        const item = additionalData?.values?.find(x => x.id === column.id);
        return !!item;
      }
      case BoardColumnType.CustomDate: {
        const { additionalData } = data.turnoverData;
        const item = additionalData?.dates?.find(x => x.id === column.id);
        return !!item;
      }
      default:
        return true;
    }
  }

  getValue(column: BoardColumn, data: RestTurnoverTaskModel) {
    const { type } = column;
    switch (type) {
      case BoardColumnType.MoveIn:
        return data.turnoverData.dateMoveIn;
      case BoardColumnType.MoveOut:
        return data.turnoverData.dateMoveOut;
      case BoardColumnType.Available:
        return data.turnoverData.dateAvailable;
      case BoardColumnType.Showable:
        return data.turnoverData.dateShowable;
      case BoardColumnType.NewRent:
        return data.turnoverData.newRentOverride ?? data.turnoverData.newRent ?? 0;
      case BoardColumnType.UnitStatus: {
        return data.turnoverData.unitStatus;
      }
      case BoardColumnType.OldRent:
        return data.turnoverData.oldRentOverride ?? data.turnoverData.oldRent ?? 0;
      case BoardColumnType.MarketRent:
        return data.turnoverData.marketRentOverride ?? data.turnoverData.marketRent ?? 0;
      case BoardColumnType.AdvertisedRent:
        return data.turnoverData.advertisedRentOverride ?? data.turnoverData.advertisedRent ?? 0;

      case BoardColumnType.PropertyName:
        return data.propertyId ?? 0;
      case BoardColumnType.NewFinish:
        return data.turnoverData.newFinish ?? 0;
      case BoardColumnType.OldFinish:
        return data.turnoverData.oldFinish ?? 0;
      case BoardColumnType.Finish:
        return data.turnoverData.newFinish ?? data.turnoverData.oldFinish ?? 0;
      case BoardColumnType.Budget:
      case BoardColumnType.CustomAmount: {
        const { additionalData } = data.turnoverData;
        const { amount } = additionalData?.amounts?.find(x => x.id === column.id) || {};
        return amount || 0;
      }
      case BoardColumnType.DropDown: {
        const { additionalData } = data.turnoverData;
        const { value } = additionalData?.values?.find(x => x.id === column.id) || {};
        return value || null;
      }
      case BoardColumnType.CustomDate: {
        const { additionalData } = data.turnoverData;
        const { date } = additionalData?.dates?.find(x => x.id === column.id) || {};
        return date || null;
      }
      case BoardColumnType.ApplicantName:
        return data.turnoverData?.applicantName || '';
      case BoardColumnType.ApplicantStatus:
        return data.turnoverData?.applicantStatus || '';
      case BoardColumnType.ApplicantDate:
        return data.turnoverData?.applicantDate || null;
      case BoardColumnType.ApplicantApprovedDate:
        return data.turnoverData?.applicantApprovedDate || null;
      case BoardColumnType.ApplicationCompletedDate:
        return data.turnoverData?.applicationCompletedDate || null;
      case BoardColumnType.LeaseApprovedDate:
        return data.turnoverData?.leaseApprovedDate || null;
      default:
        return null;
    }
  }

  getText(column: BoardColumn, data: RestTurnoverTaskModel, isProject = false): string {
    const { type } = column;
    switch (type) {
      case BoardColumnType.Unit:
        return data.unitName;
      case BoardColumnType.PropertyName:
        return data.propertyName;
      case BoardColumnType.TurnStep: {
        const step = data.turnoverData.workflow.find(
          x => x.workflowStepEnumType === data.turnoverData.activeWorkflowStepType
        );
        if (isProject) {
          if (step.workflowStepEnumType === WorkflowStepEnumType.Notice) return ProjectWorkflowStepEnumLabels.Notice;
          if (step.workflowStepEnumType === WorkflowStepEnumType.MoveOut) return ProjectWorkflowStepEnumLabels.MoveOut;
          if (step.workflowStepEnumType === WorkflowStepEnumType.MakeReady)
            return ProjectWorkflowStepEnumLabels.MakeReady;
          if (step.workflowStepEnumType === WorkflowStepEnumType.MoveIn) return ProjectWorkflowStepEnumLabels.MoveIn;
        }

        return step?.name || '';
      }
      case BoardColumnType.NewRent:
      case BoardColumnType.OldRent:
      case BoardColumnType.MarketRent:
      case BoardColumnType.AdvertisedRent:
        const value = this.getValue(column, data);
        if (value) {
          return `$${value ?? ''}`;
        }
        return null;

      case BoardColumnType.UnitType:
        return data.unitType;
      case BoardColumnType.NewTenant:
        const firstName = data.turnoverData.preleaseTenantFirstName;
        const surname = data.turnoverData.preleaseTenantSurname;
        const fullTenantName = firstName && surname ? `${firstName} ${surname}` : firstName ? firstName : surname;
        return fullTenantName;

      case BoardColumnType.Days:
        const inStep = this.daysInStep.transform(
          data.turnoverData.activeWorkflowStepType,
          data.turnoverData.timerInstance
        );
        return `${inStep.hours > 12 ? inStep.days + 1 : inStep.days}`;
      case BoardColumnType.TotalDays:
        const total = this.daysInStep.transform(WorkflowStepEnumType.MoveOut, data.turnoverData.timerInstance, true);
        return `${total.hours > 12 ? total.days + 1 : total.days}`;
      case BoardColumnType.ApplicantName:
        return data.turnoverData?.applicantName || '';
      case BoardColumnType.ApplicantStatus:
        return data.turnoverData?.applicantStatus || '';
      case BoardColumnType.ProjectType:
        return data.turnoverData?.projectType?.name || null;
      default:
        return null;
    }
  }

  getComparableValue(column: BoardColumn, data: RestTurnoverTaskModel, columnId: string): string | number {
    const { type } = column;
    switch (type) {
      case BoardColumnType.Comments:
        const comments = data.turnoverData.workflowStepComments;
        return this.prepareDateToCompare(comments[comments?.length - 1]?.dateCreated);

      case BoardColumnType.Documents:
        return data.turnoverData?.allAttachments?.length ?? 0;

      case BoardColumnType.MoveIn:
      case BoardColumnType.MoveOut:
      case BoardColumnType.Available:
      case BoardColumnType.Showable:
      case BoardColumnType.CustomDate:
      case BoardColumnType.ApplicantDate:
      case BoardColumnType.ApplicantApprovedDate:
      case BoardColumnType.ApplicationCompletedDate:
      case BoardColumnType.LeaseApprovedDate:
        return this.prepareDateToCompare(this.getValue(column, data) as Date);

      case BoardColumnType.NewRent:
      case BoardColumnType.OldRent:
      case BoardColumnType.MarketRent:
      case BoardColumnType.AdvertisedRent:
      case BoardColumnType.CustomAmount:
        return this.getValue(column, data) as number;

      case BoardColumnType.Unit:
      case BoardColumnType.PropertyName:
      case BoardColumnType.UnitType:
      case BoardColumnType.NewTenant:
        return this.prepareStringToCompare(this.getText(column, data));

      case BoardColumnType.Assignee:
        return this.prepareStringToCompare(data?.assigneeName);

      case BoardColumnType.NewFinish:
        return data.turnoverData.newFinish ?? 0;
      case BoardColumnType.OldFinish:
        return data.turnoverData.oldFinish ?? 0;
      case BoardColumnType.Finish:
        return data.turnoverData.newFinish ?? data.turnoverData.oldFinish ?? 0;

      case BoardColumnType.Simple:
      case BoardColumnType.Timed:
      case BoardColumnType.Workorder:
        const turnSchedule = data.turnoverData.turnStepSchedules.find(step => step.boardColumnUID === columnId);
        if (
          turnSchedule?.status === ScheduleCellStatus.Skipped ||
          turnSchedule?.status === ScheduleCellStatus.Declined ||
          turnSchedule?.status === ScheduleCellStatus.Pending
        ) {
          return 0;
        }
        const dueDate = turnSchedule?.dueDate;
        const completedDate = turnSchedule?.dateCompleted;
        return this.prepareDateToCompare(
          turnSchedule?.status === ScheduleCellStatus.Done && completedDate ? completedDate : dueDate
        );

      case BoardColumnType.Days:
        return getTimeInStep(data.turnoverData.activeWorkflowStepType, data.turnoverData.timerInstance);
      case BoardColumnType.TotalDays:
        return getTimeInStep(WorkflowStepEnumType.MoveOut, data.turnoverData.timerInstance);

      case BoardColumnType.DropDown: {
        const { options } = column;
        const option = options.find(o => o.uuid === this.getValue(column, data));
        return option?.title || '';
      }
      case BoardColumnType.ApplicantName:
        return this.prepareStringToCompare(data.turnoverData?.applicantName);
      case BoardColumnType.ApplicantStatus:
        return this.prepareStringToCompare(data.turnoverData?.applicantStatus);

      case BoardColumnType.TurnDescription:
        return this.prepareStringToCompare(data.turnoverData?.turnDescription);
      case BoardColumnType.Notes:
        const notes = data.turnoverData.additionalData?.values?.find(x => x.id === column.id);
        return this.prepareStringToCompare(notes?.value);

      default:
        return null;
    }
  }

  getColumnTotal(column: BoardColumn, data: RestTurnoverTaskModel[]): number {
    let value = 0;
    switch (column.type) {
      case BoardColumnType.Budget:
      case BoardColumnType.CustomAmount: {
        value = sum(
          data.map(turnover => {
            return this.getValue(column, turnover) as number;
          })
        );
        break;
      }
      case BoardColumnType.Simple: {
        value = sum(
          data.map(turnover => {
            const schedule = turnover.turnoverData.turnStepSchedules.find(x => x.boardColumnUID === column.id);
            return schedule ? schedule.amount : 0;
          })
        );
        break;
      }
    }

    return value ? Math.round(value * 100) / 100 : 0;
  }

  getCalculatedValue(columnType: BoardColumnType, allColumns: BoardColumn[], data: RestTurnoverTaskModel): number {
    let value = 0;
    switch (columnType) {
      case BoardColumnType.Budget: {
        const budgetColumn = allColumns.find(x => x.type === BoardColumnType.Budget);
        value = this.getValue(budgetColumn, data) as number;
        break;
      }
      case BoardColumnType.CustomAmount:
      case BoardColumnType.UnitTotal: {
        value = this.calculateCostColumnValue(allColumns, data);
        break;
      }
      case BoardColumnType.Delta: {
        const costValue = this.calculateCostColumnValue(allColumns, data);
        const budget = allColumns.find(x => x.type === BoardColumnType.Budget);
        const budgetValue = budget ? this.getValue(budget, data) : 0;
        value = budget ? budgetValue - costValue : 0;
        break;
      }
    }
    return value;
  }

  calculateCostColumnValue(allColumns: BoardColumn[], data: RestTurnoverTaskModel): number {
    const summableColumns = allColumns.filter(isCostColumn);
    return summableColumns.reduce((acc, column) => {
      if (column.type === BoardColumnType.Simple) {
        const schedule = data.turnoverData?.turnStepSchedules?.find(x => x.boardColumnUID === column.id);
        return schedule && schedule.amount ? schedule.amount + acc : acc;
      }
      return this.getValue(column, data) + acc;
    }, 0);
  }

  compareCalculatedValues(
    columnType: BoardColumnType,
    summableColumns: BoardColumn[],
    data1: RestTurnoverTaskModel,
    data2: RestTurnoverTaskModel,
    direction: Sorting
  ): number {
    const isUnitOnHold1 = data1.turnoverData?.isUnitOnHold ?? false;
    const isUnitOnHold2 = data2.turnoverData?.isUnitOnHold ?? false;
    const isUnitDown1 = data1.turnoverData?.isUnitDown ?? false;
    const isUnitDown2 = data2.turnoverData?.isUnitDown ?? false;

    const getPriority = (isDown: boolean, isHold: boolean): number => {
      if (isHold) return 2;
      if (isDown) return 1;
      return 0;
    };
    const priority1 = getPriority(isUnitDown1, isUnitOnHold1);
    const priority2 = getPriority(isUnitDown2, isUnitOnHold2);
    if (priority1 !== priority2) {
      return priority1 - priority2;
    }

    const value1 = this.getCalculatedValue(columnType, summableColumns, data1);
    const value2 = this.getCalculatedValue(columnType, summableColumns, data2);
    const dir = direction === 'ascending' ? 1 : -1;
    return value1 === value2 ? 0 : (value1 > value2 ? 1 : -1) * dir;
  }

  private prepareDateToCompare(value: Date | string) {
    return new Date(value ?? null).getTime();
  }

  private prepareStringToCompare(value: string) {
    return value?.trim().toLowerCase() ?? '';
  }
}
